/**
 * useToast - minimal, self-contained toast notifications for the user portal.
 *
 * There is no global <Toaster> mounted in the app, so this hook renders toasts
 * by appending a transient, self-removing DOM node to a fixed container. Keeps
 * the shadcn-style `toast({ title, description, variant })` call shape used by
 * feature components without requiring a provider.
 */

type ToastVariant = 'default' | 'destructive';

interface ToastOptions {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
}

const CONTAINER_ID = 'pp-toast-container';

function getContainer(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  let container = document.getElementById(CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = CONTAINER_ID;
    container.style.cssText =
      'position:fixed;top:16px;right:16px;z-index:9999;display:flex;flex-direction:column;gap:8px;max-width:360px;';
    document.body.appendChild(container);
  }
  return container;
}

function showToast({ title, description, variant = 'default', durationMs = 4000 }: ToastOptions) {
  const container = getContainer();
  if (!container) return;

  const el = document.createElement('div');
  const isError = variant === 'destructive';
  el.style.cssText = [
    'padding:12px 16px',
    'border-radius:8px',
    'box-shadow:0 4px 12px rgba(0,0,0,0.15)',
    'font-size:14px',
    'line-height:1.4',
    `background:${isError ? '#FEF2F2' : '#111827'}`,
    `color:${isError ? '#991B1B' : '#FFFFFF'}`,
    `border:1px solid ${isError ? '#FECACA' : 'transparent'}`,
    'opacity:0',
    'transform:translateY(-8px)',
    'transition:opacity 150ms ease, transform 150ms ease',
  ].join(';');

  if (title) {
    const t = document.createElement('div');
    t.style.fontWeight = '600';
    t.textContent = title;
    el.appendChild(t);
  }
  if (description) {
    const d = document.createElement('div');
    d.style.opacity = '0.9';
    d.textContent = description;
    el.appendChild(d);
  }

  container.appendChild(el);
  requestAnimationFrame(() => {
    el.style.opacity = '1';
    el.style.transform = 'translateY(0)';
  });

  const remove = () => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(-8px)';
    setTimeout(() => el.remove(), 200);
  };
  setTimeout(remove, durationMs);
  el.addEventListener('click', remove);
}

export function useToast() {
  return { toast: showToast };
}

export const toast = showToast;
